import apiClient from "./client";

export interface ApiCategory {
  id: number;
  name: string;
  parent_category: string | null;
  plaid_category: string | null;
  icon: string | null;
  is_system: boolean;
}

/** Who wrote the row: the bank feed, or the user by hand. Only a manual row can
 *  have its date, amount and description edited, or be deleted. */
export type TransactionSource = "plaid" | "manual";

export interface ApiTransaction {
  id: number;
  transaction_id: string;
  /** Null on a manual row: it is not held by any linked account. */
  bank_account: number | null;
  amount: number;
  date: string;
  name: string;
  merchant_name: string | null;
  merchant_name_normalized: string | null;
  plaid_category_primary: string | null;
  plaid_category_detailed: string | null;
  category: ApiCategory | null;
  parent_category: string | null;
  is_pending: boolean;
  is_recurring: boolean;
  is_income: boolean;
  needs_review: boolean;
  /** Contract batch 15 (SC17): the other person on a P2P row, as the bank's
   *  descriptor spells it; null when the row isn't P2P or names nobody. Optional:
   *  older payloads have none. */
  counterparty?: string | null;
  /** Optional: fixtures that predate manual rows omit it, and absent reads as a bank row. */
  source?: TransactionSource;
  created_at: string;
  updated_at: string;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/** Server-side ordering tokens accepted by the backend (#387). */
export type TransactionOrdering = "date" | "-date" | "amount" | "-amount";

export interface TransactionListParams {
  page?: number;
  page_size?: number;
  account?: number;
  // A parent key ("needs" | "wants" | "savings" | …). Typed `number` until Phase 2,
  // which first sent it; the server filters `parent_category=<key>`.
  parent_category?: string;
  category?: number;
  date_from?: string;
  date_to?: string;
  amount_min?: number;
  amount_max?: number;
  is_income?: boolean;
  is_recurring?: boolean;
  needs_review?: boolean;
  search?: string;
  ordering?: TransactionOrdering;
}

export interface UpdateTransactionData {
  category?: number | null;
  parent_category?: string | null;
}

/** `ManualTransactionSerializer`. `amount` is the magnitude the user typed;
 *  `direction` says which way the money went. */
export interface ManualTransactionData {
  date: string;
  amount: string;
  direction: "expense" | "income";
  name: string;
  category: number | null;
}

/** Serialize the shared filter params (everything except pagination/ordering). */
function appendFilterParams(query: URLSearchParams, params: TransactionListParams): void {
  if (params.account != null) query.set("account", String(params.account));
  if (params.parent_category != null) query.set("parent_category", String(params.parent_category));
  if (params.category != null) query.set("category", String(params.category));
  if (params.date_from) query.set("date_from", params.date_from);
  if (params.date_to) query.set("date_to", params.date_to);
  if (params.amount_min != null) query.set("amount_min", String(params.amount_min));
  if (params.amount_max != null) query.set("amount_max", String(params.amount_max));
  if (params.is_income != null) query.set("is_income", String(params.is_income));
  if (params.is_recurring != null) query.set("is_recurring", String(params.is_recurring));
  if (params.needs_review != null) query.set("needs_review", String(params.needs_review));
  if (params.search) query.set("search", params.search);
}

export async function getTransactions(
  params: TransactionListParams = {},
): Promise<PaginatedResponse<ApiTransaction>> {
  const query = new URLSearchParams();
  if (params.page) query.set("page", String(params.page));
  if (params.page_size) query.set("page_size", String(params.page_size));
  appendFilterParams(query, params);
  if (params.ordering) query.set("ordering", params.ordering);

  const qs = query.toString();
  const { data } = await apiClient.get<PaginatedResponse<ApiTransaction>>(
    `/transactions/${qs ? `?${qs}` : ""}`,
  );
  return data;
}

export interface TransactionSummary {
  total_income: string;
  total_expenses: string;
  net_balance: string;
  transaction_count: number;
  needs_review_count: number;
}

export async function getTransactionSummary(
  params: Omit<TransactionListParams, "page" | "page_size"> = {},
): Promise<TransactionSummary> {
  const query = new URLSearchParams();
  appendFilterParams(query, params);

  const qs = query.toString();
  const { data } = await apiClient.get<TransactionSummary>(
    `/transactions/summary/${qs ? `?${qs}` : ""}`,
  );
  return data;
}

export async function getTransaction(id: number): Promise<ApiTransaction> {
  const { data } = await apiClient.get<ApiTransaction>(`/transactions/${id}/`);
  return data;
}

export async function updateTransaction(
  id: number,
  updateData: UpdateTransactionData,
): Promise<ApiTransaction> {
  const { data } = await apiClient.patch<ApiTransaction>(`/transactions/${id}/`, updateData);
  return data;
}

export async function createTransaction(body: ManualTransactionData): Promise<ApiTransaction> {
  const { data } = await apiClient.post<ApiTransaction>("/transactions/", body);
  return data;
}

/** Edit a manual row. A bank row refuses these fields (400). */
export async function updateManualTransaction(
  id: number,
  body: Partial<ManualTransactionData>,
): Promise<ApiTransaction> {
  const { data } = await apiClient.patch<ApiTransaction>(`/transactions/${id}/`, body);
  return data;
}

/** Delete a manual row. A bank row cannot be deleted (400). */
export async function deleteTransaction(id: number): Promise<void> {
  await apiClient.delete(`/transactions/${id}/`);
}

/** Pages fetched at most, so a runaway `next` cannot loop. 40 × 50 = 2,000 rows. */
const UNKNOWN_MAX_PAGES = 40;

/**
 * The whole review queue, every page (CAT-013). This returned page 1 only, 50
 * rows at the default page size, so an account with 107 rows to review could
 * never finish the queue from the wizard.
 */
export async function getUnknownTransactions(): Promise<ApiTransaction[]> {
  const rows: ApiTransaction[] = [];
  for (let page = 1; page <= UNKNOWN_MAX_PAGES; page++) {
    const { data } = await apiClient.get<PaginatedResponse<ApiTransaction>>(
      "/transactions/unknown/",
      { params: { page } },
    );
    rows.push(...data.results);
    if (!data.next) break;
  }
  return rows;
}

export interface CategorizeTransactionData {
  category: number;
  parent_category?: string | null;
  create_rule?: boolean;
  apply_retroactively?: boolean;
}

export interface CategorizeTransactionResponse {
  transaction: ApiTransaction;
  /**
   * The created rule, or null. NOT a boolean — this type said `boolean` while the
   * endpoint has always returned a serialized `CategoryRule` object or null
   * (BEN-141 item 1a). Nothing read it, so the lie was latent; it is corrected
   * here because §4's merchant control is the first caller that acts on it.
   */
  rule_created: { id: number; merchant_pattern: string } | null;
  /**
   * Non-null ONLY when a rule was requested and could not be built. Per #860 a
   * null `rule_created` must NOT be read as success — that conflation is exactly
   * the bug that shipped a success toast with no rule. Callers must surface this.
   */
  rule_skipped_reason: string | null;
  /**
   * Rows the backfill rewrote, INCLUDING the one being categorised. `null` = no
   * backfill was asked for; `0` = one ran and matched nothing. Different
   * outcomes, deliberately distinguishable.
   *
   * No longer conditional on `create_rule`: "Apply to similar transactions" is an
   * independent control and now backfills on its own (BEN-141, Vinnie item 1).
   */
  recategorized_count: number | null;
  /**
   * Non-null ONLY when a backfill was requested and could not be attempted (the
   * row has no merchant name or description to match on). Same contract as
   * `rule_skipped_reason` — a null count must NOT be read as "nothing similar
   * existed", because "we could not look" and "we looked and found none" are
   * different answers.
   */
  backfill_skipped_reason: string | null;
}

export async function categorizeTransaction(
  id: number,
  catData: CategorizeTransactionData,
): Promise<CategorizeTransactionResponse> {
  const { data } = await apiClient.post<CategorizeTransactionResponse>(
    `/transactions/${id}/categorize/`,
    catData,
  );
  return data;
}
